/**
 * Pure helpers for the Projects "Lanes" layout.
 *
 * Lanes are scheduling-first, then status:
 *   Backlog  = unscheduled (any status)
 *   Planned  = scheduled + planned
 *   Active   = scheduled + active/almost
 *   Done     = scheduled + done
 */
import type { Project, ProjectStatus, Task, TaskPriority } from './types';
import { getWeekEnd, getWeekStart } from './utils';

export type ProjectLane = 'backlog' | 'planned' | 'active' | 'done';

export const PROJECT_LANES: { key: ProjectLane; label: string; icon: string }[] = [
  { key: 'backlog', label: 'Backlog', icon: '📥' },
  { key: 'planned', label: 'Planned', icon: '📋' },
  { key: 'active', label: 'Active', icon: '🔄' },
  { key: 'done', label: 'Done', icon: '✅' },
];

export const COLLAPSED_LANES_STORAGE_KEY = 'projectIndex:collapsedLanes';
export const COLLAPSED_LANE_WIDTH = '2.75rem';

const LANE_KEYS = new Set<ProjectLane>(PROJECT_LANES.map((lane) => lane.key));

export function parseCollapsedLanes(raw: unknown): Set<ProjectLane> {
  if (!Array.isArray(raw)) return new Set();

  const collapsed = new Set<ProjectLane>();
  for (const item of raw) {
    if (typeof item === 'string' && LANE_KEYS.has(item as ProjectLane)) {
      collapsed.add(item as ProjectLane);
    }
  }

  // All four collapsed is unusable; treat it as a corrupt preference.
  if (collapsed.size >= PROJECT_LANES.length) return new Set();
  return collapsed;
}

export function loadCollapsedLanes(storage?: Storage | null): Set<ProjectLane> {
  if (!storage) return new Set();
  try {
    const raw = storage.getItem(COLLAPSED_LANES_STORAGE_KEY);
    if (!raw) return new Set();
    return parseCollapsedLanes(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

export function persistCollapsedLanes(
  collapsed: ReadonlySet<ProjectLane>,
  storage?: Storage | null,
): void {
  if (!storage) return;
  storage.setItem(COLLAPSED_LANES_STORAGE_KEY, JSON.stringify([...collapsed]));
}

export function canCollapseLane(collapsed: ReadonlySet<ProjectLane>, lane: ProjectLane): boolean {
  if (collapsed.has(lane)) return false;
  return collapsed.size < PROJECT_LANES.length - 1;
}

export function toggleCollapsedLane(
  collapsed: ReadonlySet<ProjectLane>,
  lane: ProjectLane,
): Set<ProjectLane> {
  const next = new Set(collapsed);
  if (next.has(lane)) {
    next.delete(lane);
    return next;
  }
  if (!canCollapseLane(collapsed, lane)) return next;
  next.add(lane);
  return next;
}

export function laneGridTemplate(collapsed: ReadonlySet<ProjectLane>): string {
  return PROJECT_LANES.map((lane) =>
    collapsed.has(lane.key) ? COLLAPSED_LANE_WIDTH : 'minmax(0, 1fr)',
  ).join(' ');
}

const SCHEDULED_LANES: ProjectLane[] = ['planned', 'active', 'done'];

export function laneGroupLabels(collapsed: ReadonlySet<ProjectLane>): {
  unscheduled: boolean;
  scheduled: boolean;
} {
  return {
    unscheduled: !collapsed.has('backlog'),
    scheduled: SCHEDULED_LANES.some((lane) => !collapsed.has(lane)),
  };
}

export function statusForLane(lane: ProjectLane): ProjectStatus | null {
  if (lane === 'backlog') return null;
  if (lane === 'planned') return 'planned';
  if (lane === 'active') return 'active';
  return 'done';
}

export type LaneMove =
  | { type: 'none' }
  | { type: 'backlog' }
  | { type: 'status'; status: ProjectStatus }
  | { type: 'schedule'; weekKey: string; status: ProjectStatus };

export function resolveLaneMove(
  project: Project,
  targetLane: ProjectLane,
  weekKey: string,
): LaneMove {
  const current = laneForProject(project);
  if (!current || current === targetLane) return { type: 'none' };
  if (targetLane === 'backlog') return { type: 'backlog' };

  const status = statusForLane(targetLane);
  if (!status) return { type: 'none' };
  if (current === 'backlog') return { type: 'schedule', weekKey, status };
  return { type: 'status', status };
}

export function scheduledDatesForWeek(weekKey: string): {
  startDate: string;
  endDate: string;
  targetDate: string;
  monthKey: string;
} {
  const start = getWeekStart(weekKey);
  const end = getWeekEnd(weekKey);
  const startDate = start.toISOString().slice(0, 10);
  return {
    startDate,
    endDate: end.toISOString().slice(0, 10),
    targetDate: startDate,
    monthKey: startDate.slice(0, 7),
  };
}

export const PREVIEW_TASK_LIMIT = 3;

const PRIORITY_ORDER: Record<TaskPriority, number> = {
  high: 0,
  medium: 1,
  low: 2,
  none: 3,
};

// Timeline Days panel ordering: active/almost first, then planned.
const PANEL_STATUS_ORDER: Partial<Record<ProjectStatus, number>> = {
  active: 0,
  almost: 0,
  planned: 1,
};
const PANEL_STATUS_ORDER_MAX = 2;

export function laneForProject(project: Project): ProjectLane | null {
  if (project.isArchived) return null;
  if (project.schedulingState === 'backlog') return 'backlog';
  if (project.status === 'done') return 'done';
  if (project.status === 'planned') return 'planned';
  return 'active';
}

export function groupProjectsByLane(projects: Project[]): Record<ProjectLane, Project[]> {
  const groups: Record<ProjectLane, Project[]> = {
    backlog: [],
    planned: [],
    active: [],
    done: [],
  };

  for (const project of projects) {
    const lane = laneForProject(project);
    if (lane) groups[lane].push(project);
  }

  return groups;
}

export type TaskPreview = { id: string; title: string; priority: TaskPriority };

/** Server preview is already priority then recency — just cap it. */
export function previewTasksFromIncomplete(
  incomplete: TaskPreview[] | undefined,
  limit: number = PREVIEW_TASK_LIMIT,
): TaskPreview[] {
  return (incomplete ?? []).slice(0, limit);
}

/** Live cache: incomplete only, priority then createdAt desc. */
export function previewTasksFromCached(
  tasks: Task[],
  limit: number = PREVIEW_TASK_LIMIT,
): TaskPreview[] {
  return tasks
    .filter((task) => task.status === 'todo')
    .sort((a, b) => {
      const priorityDiff = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
      if (priorityDiff !== 0) return priorityDiff;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    })
    .slice(0, limit)
    .map((task) => ({ id: task.id, title: task.title, priority: task.priority }));
}

/**
 * Projects for the Timeline Days view's global panel: scheduled + planned/active only
 * (no backlog, done, or archived). Active/almost come before planned; priority projects
 * lead within each group. Ties keep the caller's order, which the server makes
 * deterministic via Project.by_priority (is_priority desc, created_at asc).
 */
export function timelinePanelProjects(projects: Project[]): Project[] {
  return projects
    .filter(
      (project) =>
        !project.isArchived &&
        project.schedulingState === 'scheduled' &&
        (project.status === 'planned' ||
          project.status === 'active' ||
          project.status === 'almost'),
    )
    .sort((a, b) => {
      const statusDiff =
        (PANEL_STATUS_ORDER[a.status] ?? PANEL_STATUS_ORDER_MAX) -
        (PANEL_STATUS_ORDER[b.status] ?? PANEL_STATUS_ORDER_MAX);
      if (statusDiff !== 0) return statusDiff;
      if (a.isPriority !== b.isPriority) return a.isPriority ? -1 : 1;
      return 0;
    });
}
